import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { TouchpointsService } from '../../touchpoints/services/touchpoints.service';
import { AccountNote } from '../entities/accountNote';
import { NotesWidgetService } from './notes-widget.service';

@Component({
  selector: 'app-notes-widget',
  templateUrl: './notes-widget.component.html',
  styleUrls: ['./notes-widget.component.scss'],
  standalone: false
})
export class NotesWidgetComponent implements OnInit, OnDestroy {

  formNotes: UntypedFormGroup;
  notesData: AccountNote[] = [];
  deletedNotesData: any[] = [];
  appStrings: AppLocalization;
  notesList = new Subject();
  @Input() accountId: string;
  @Input() canOpenTouchpoints = false;
  @Output() loaded = new EventEmitter<void>();
  @Output() openTouchpoint = new EventEmitter<AccountNote>();
  dateTimeFormat = this.appInfoService.dateTimeFormat;

  deleteConfirmationOpen$ = new EventEmitter<boolean>();
  selectedNoteIndex: number;
  deleteConfirmationButtons = [
    { label: 'Cancel', type: 'secondary' },
    { label: 'Yes', type: 'primary', onclick: this.confirmDelete.bind(this) },
  ];
  private touchpointNotesSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private notesService: NotesWidgetService,
    private touchpointsService: TouchpointsService
  ) { }

  ngOnInit(): void {
    this.appStrings = this.appInfoService.appStrings;
    this.initForm();
    this.loadNotes();
    this.touchpointNotesSubscription = this.touchpointsService.touchpointNotesChanged$.subscribe(() => this.loadNotes());
  }

  ngOnDestroy(): void {
    this.touchpointNotesSubscription?.unsubscribe();
  }

  private initForm() {
    this.formNotes = this.fb.group({
      notes: ['']
    });
  }

  private loadNotes() {
    this.notesService.getAccountNotes(this.accountId, result => {
      this.setNotes(result);
      this.loaded.emit();
    }, () => this.loaded.emit());
  }

  /**
   * Setting notes data
   * @param notesTemplate saved account notes
   */
  setNotes(notesTemplate: AccountNote[]) {
    this.notesData = notesTemplate || [];
  }

  isTouchpointNote(note: AccountNote): boolean {
    return !!note.touchpointId;
  }

  canOpenTouchpoint(note: AccountNote): boolean {
    return this.canOpenTouchpoints && !note.isTouchpointDeleted;
  }

  getTouchpointChipLabel(note: AccountNote): string {
    const label = this.appStrings.label_touchpointNote;
    return note.touchpointSource ? `${label} (${note.touchpointSource})` : label;
  }

  /**
   * Adding an account note
   */
  addNote() {
    const note = this.formNotes.controls.notes.value;
    const noteModel: AccountNote = {
      noteDate: new Date(),
      noteText: note,
      accountNoteId: '',
      author: ''
    };
    this.notesService.saveAccountsNotes(this.accountId, noteModel.noteText).subscribe(result => {
      noteModel.accountNoteId = result.id;
      noteModel.author = result.author;
      this.notesData = [noteModel, ...this.notesData];
    });
    this.formNotes.controls.notes.reset();
  }

  /**
   * Deleting an account note
   * @param index note index
   */
  deleteNote(index: number) {
    this.selectedNoteIndex = index;
    this.deleteConfirmationOpen$.emit(true);
  }

  /**
   * Confirm and execute the delete operation
   */
  confirmDelete() {
    this.notesService.deleteAccountsNotes(this.notesData[this.selectedNoteIndex].accountNoteId).subscribe(
      () => {
        this.notesData.splice(this.selectedNoteIndex, 1);
        this.notesData = this.notesData.slice(0);
        this.selectedNoteIndex = null;
      });
  }
}
